import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  FormDialog,
  Grid,
  InputField,
  MuiBox,
  SelectDropdown,
  Switch,
  Typography
} from '@kis-technologies/kis-ui';
import { FeeInput } from 'components/FeeInput';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors } from 'store/auth';
import { selectors as ciSelectors, actions as ciActions } from 'store/clientIntegrations';
import { actions as venuesActions, selectors as venuesSelectors } from 'store/venues';
import { toDecimal, toPercentage } from 'utils/number';
import { getSortedList } from 'utils/stringFormat';
import VenueTaxesTable from './components/VenueTaxTable';
import { VenueTaxOverlay } from './components/VenueTaxOverlay';
import { useStyles } from './styled';
import { PaymentsFormData, PaymentsViewProps, VenueTax, VenueTaxData } from './types';
import { gatewayOptions, LOCKED_FIELD_KEYS } from './utils';

const PaymentsView = ({ setHasUnsavedChanges }: PaymentsViewProps) => {
  const initialData: PaymentsFormData = {
    kisIsMerchant: true,
    customerIsMerchant: false,
    gateway: null,
    accountId: '',
    apiKeyId: '',
    apiKeySecret: '',
    apiAccessKey: '',
    terminalId: '',
    secretKey: '',
    apiKey: '',
    cardProcessingFee: { amount: null, percentage: null },
    kisTransactionFee: { amount: null, percentage: null },
    defaultProductFee: { amount: null, percentage: null },
    allowRefunds: false,
    venueTaxes: [],
    isLive: null
  };
  const [formData, setFormData] = useState<PaymentsFormData>(initialData);
  const [environmentId, setEnvironmentId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [savingFormData, setSavingFormData] = useState<PaymentsFormData | null>(null);
  const [venueOverlayOpen, setVenueOverlayOpen] = useState(false);
  const [venueEditData, setVenueEditData] = useState<VenueTaxData>();
  const [venueActionType, setVenueActionType] = useState<'create' | 'edit'>();
  const [resetData, setResetData] = useState<PaymentsFormData>(initialData);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showRiskyChangeWarning, setShowRiskyChangeWarning] = useState(false);

  const hasTransactions = useSelector(ciSelectors.selectHasTransactions);
  const paymentSettings = useSelector(ciSelectors.selectPaymentSettings);
  const admin = useSelector(authSelectors.selectAdmin);
  const venues = useSelector(venuesSelectors.selectVenues);
  const clientIntegrations = useSelector(ciSelectors.selectClientIntegrations);
  const action = useSelector(ciSelectors.selectAction);

  const kisTechnologiesIntegrations = clientIntegrations?.filter((ci) => ci.integration?.code === 'kistechnologies');
  const sortedEnvironments = getSortedList(kisTechnologiesIntegrations, 'name');

  const [getClientIntegrations, getPaymentSettings, updatePaymentSettings, resetAction] = useActions([
    ciActions.getClientIntegrations,
    ciActions.getPaymentSettings,
    ciActions.updatePaymentSettings,
    ciActions.resetAction
  ]);
  const [getVenues] = useActions([venuesActions.getVenues]);

  const role = admin?.role;
  const isClientAdmin = role === 'ClientAdmin';
  const isGlobalAdmin = role === 'GlobalAdmin';
  const canEditPaymentSettings = isClientAdmin || isGlobalAdmin;
  const canEditGatewaySettings = isGlobalAdmin;

  useEffect(() => {
    getClientIntegrations();
    getVenues();
  }, []);

  useEffect(() => {
    if (environmentId) {
      getPaymentSettings(environmentId);
    }
  }, [environmentId]);

  useEffect(() => {
    if (!environmentId && sortedEnvironments.length) {
      setEnvironmentId(sortedEnvironments[0].id);
    }
  }, [sortedEnvironments]);

  const classes = useStyles();

  useEffect(() => {
    const isEqual = JSON.stringify(formData) === JSON.stringify(resetData);
    setIsDisabled(isEqual);
    setHasUnsavedChanges(!isEqual);
  }, [formData, resetData]);

  // ON UPDATE PAYMENT SETTINGS SUCCESS/ERROR
  useEffect(() => {
    if (action.error) {
      setErrorMessage('Uh oh! Something went wrong. Please try again.');
      setSavingFormData(null);
      resetAction();
      return;
    }

    if (savingFormData && action.success) {
      setResetData(savingFormData);
      setSavingFormData(null);
      resetAction();
    }
  }, [action.error, action.success]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapApiToForm = (paymentSetting: any) => {
    const isNewEnvironment = !paymentSetting?.gatewayCode;

    return {
      gateway: (paymentSetting?.gatewayCode as 'payConex' | 'payConexPlus') ?? null,
      kisIsMerchant: isNewEnvironment ? true : paymentSetting.isKisMerchantAccount,
      customerIsMerchant: isNewEnvironment ? false : !paymentSetting.isKisMerchantAccount,
      allowRefunds: paymentSetting?.allowRefunds ?? false,
      terminalId: paymentSetting?.gatewayDetails?.terminalId ?? '',
      apiKey: paymentSetting?.gatewayDetails?.apiKey ?? '',
      secretKey: paymentSetting?.gatewayDetails?.secretKey ?? '',
      accountId: paymentSetting?.gatewayDetails?.accountId ?? '',
      apiKeyId: paymentSetting?.gatewayDetails?.apiKeyId ?? '',
      apiKeySecret: paymentSetting?.gatewayDetails?.apiKeySecret ?? '',
      apiAccessKey: paymentSetting?.gatewayDetails?.apiAccessKey ?? '',
      kisTransactionFee: {
        amount: paymentSetting?.kisTransactionFees?.[0]?.amount ?? null,
        percentage: toPercentage(paymentSetting?.kisTransactionFees?.[0]?.percentage)
      },

      defaultProductFee: {
        amount: paymentSetting?.defaultKisProductFee?.amount ?? null,
        percentage: toPercentage(paymentSetting?.defaultKisProductFee?.percentage)
      },

      cardProcessingFee: {
        amount: paymentSetting?.cardProcessingFeeMarkup?.amount ?? null,
        percentage: toPercentage(paymentSetting?.cardProcessingFeeMarkup?.percentage)
      },

      venueTaxes:
        paymentSetting?.venueTaxes?.map((tax: VenueTax) => ({
          ...tax,
          percentage: toPercentage(tax.percentage)
        })) ?? [],
      isLive: paymentSetting?.gatewayDetails?.isLive ?? null
    };
  };

  useEffect(() => {
    const mappedData = mapApiToForm(paymentSettings);
    setFormData(mappedData);
    setResetData(mappedData);
  }, [paymentSettings]);

  const updateVenueTax = (prev: PaymentsFormData, newTax: VenueTaxData): PaymentsFormData => {
    if (!newTax.venueId) return prev;

    let updatedTaxes: VenueTax[];

    if (venueActionType === 'edit' && venueEditData) {
      updatedTaxes = prev.venueTaxes.map((tax) =>
        tax.venueId === venueEditData.venueId ? { ...tax, ...newTax, venueId: newTax.venueId } : tax
      );
    } else {
      updatedTaxes = [...prev.venueTaxes, { ...newTax, venueId: newTax.venueId }];
    }

    return {
      ...prev,
      venueTaxes: updatedTaxes
    };
  };

  const buildPaymentPayload = (data: PaymentsFormData) => {
    const basePayload = {
      venueTaxes: data.venueTaxes.map((tax) => ({
        label: tax.label,
        venueId: tax.venueId,
        percentage: toDecimal(tax.percentage)
      })),
      allowRefunds: data.allowRefunds,
      kisTransactionFees: [
        {
          amount: data.kisTransactionFee.amount ?? null,
          percentage: toDecimal(data.kisTransactionFee.percentage)
        }
      ],
      defaultKisProductFee: {
        amount: data.defaultProductFee.amount ?? null,
        percentage: toDecimal(data.defaultProductFee.percentage)
      }
    };

    if (!isGlobalAdmin) {
      return basePayload;
    }

    return {
      ...basePayload,
      gatewayCode: data.gateway,
      gatewayDetails:
        data.gateway === 'payConex'
          ? {
              accountId: data.accountId || null,
              apiKeyId: data.apiKeyId || null,
              apiKeySecret: data.apiKeySecret || null,
              apiAccessKey: data.apiAccessKey || null,
              isLive: data.isLive
            }
          : {
              apiKey: data.apiKey || null,
              secretKey: data.secretKey || null,
              terminalId: data.terminalId || null,
              isLive: data.isLive
            },
      isKisMerchantAccount: data.kisIsMerchant,
      cardProcessingFeeMarkup: {
        amount: data.cardProcessingFee.amount ?? null,
        percentage: toDecimal(data.cardProcessingFee.percentage)
      }
    };
  };

  const submitPaymentSettings = () => {
    const payload = buildPaymentPayload(formData);
    updatePaymentSettings({
      id: environmentId,
      data: payload
    });
    setSavingFormData(formData);
  };

  const handleSave = () => {
    if (!environmentId) return;
    if (formData.gateway && formData.isLive === null) {
      setShowWarning(true);
      return;
    }

    // Trigger a warning if the environment has transactions, the gateway is configured, and any locked fields have changed
    const isGatewayConfigured = !!paymentSettings;
    const hasLockedFieldChanges = LOCKED_FIELD_KEYS.some((key) => formData[key] !== resetData[key]);
    if (hasTransactions && isGatewayConfigured && hasLockedFieldChanges) {
      setShowRiskyChangeWarning(true);
      return;
    }

    submitPaymentSettings();
  };

  const handleReset = () => {
    setFormData(resetData);
  };

  const availableVenues = venues.filter(
    (venue) => !formData.venueTaxes.some((tax) => tax.venueId === venue.id && venueActionType !== 'edit')
  );

  const isSavable = () => {
    if (!environmentId) return false;
    if (canEditGatewaySettings) {
      if (!formData.gateway) return false;
      if (formData.gateway && formData.isLive === null) return false;
      if (formData.gateway === 'payConex') {
        if (!formData.accountId || !formData.apiKeyId || !formData.apiKeySecret) return false;
      }
      if (formData.gateway === 'payConexPlus') {
        if (!formData.terminalId || !formData.apiKey || !formData.secretKey) return false;
      }
    }
    return true;
  };

  return (
    <MuiBox display="flex" flexDirection="column" height="100%">
      {/* Error Overlay */}
      {errorMessage && (
        <FormDialog
          cancelButtonAction={() => setErrorMessage(null)}
          confirmButtonAction={() => setErrorMessage(null)}
          confirmButtonText="Ok"
          open={!!errorMessage}
        >
          <Typography fontSize={24} maxWidth="524px">
            {errorMessage}
          </Typography>
        </FormDialog>
      )}
      <Grid className={classes.paymentsHeaderBlock} data-testid="payments-page">
        <Box flexOverride="none">
          <Typography className={classes.paymentsHeading}>Payments</Typography>
        </Box>
        <MuiBox display="flex" alignItems="end" gap="10px">
          <Typography sx={{ marginBottom: '10px', fontSize: '16px', fontWeight: 'bold' }}>Environment</Typography>
          <SelectDropdown
            placeholder=""
            items={sortedEnvironments}
            itemToString={(item) => item?.name || ''}
            selectedItem={sortedEnvironments.find((i) => i.id === environmentId) ?? null}
            onChange={(item) => setEnvironmentId(item?.id ?? null)}
            radius="round"
            width="262px"
            sx={{
              boxShadow: 'none',
              height: '45px',
              '& .MuiSelect-select': {
                color: environmentId ? '#000' : '#9E9E9E'
              }
            }}
            disabled={!sortedEnvironments.length}
          />
        </MuiBox>
      </Grid>
      <MuiBox padding="0px 40px 40px 40px" className={classes.formInnerContainer}>
        <Box raised flexOverride="1 0 0" padding="24px" sx={{ borderRadius: '4px', overflowY: 'auto' }}>
          {canEditGatewaySettings && (
            <MuiBox className={classes.formBox} sx={{ height: '125px !important' }}>
              <Typography className={classes.formLabel}>Merchant Type</Typography>
              <Box display="flex" flexDirection="column" gap="12px">
                <Checkbox
                  label="KIS is the merchant"
                  checked={formData.kisIsMerchant}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      kisIsMerchant: e.target.checked,
                      customerIsMerchant: false
                    }));
                  }}
                  size="small"
                />

                <Checkbox
                  label="The customer is the merchant"
                  checked={formData.customerIsMerchant}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      customerIsMerchant: e.target.checked,
                      kisIsMerchant: false
                    }));
                  }}
                  size="small"
                />
              </Box>
            </MuiBox>
          )}
          {canEditGatewaySettings && (
            <MuiBox className={classes.formBox}>
              <Typography className={classes.formLabel}>Payment Gateway</Typography>

              <SelectDropdown
                items={gatewayOptions}
                itemToString={(item) => item?.name || ''}
                selectedItem={gatewayOptions.find((g) => g.id === formData.gateway) ?? null}
                onChange={(item) =>
                  setFormData((prev) => ({
                    ...prev,
                    gateway: item?.id ?? null
                  }))
                }
                width="262px"
                radius="round"
                sx={{
                  height: '45px',
                  '& .MuiSelect-select': {
                    color: formData.gateway ? '#000' : '#9E9E9E'
                  }
                }}
              />
            </MuiBox>
          )}
          {canEditGatewaySettings && formData.gateway && (
            <MuiBox className={classes.formBox} sx={{ height: '125px!important' }}>
              <Typography className={classes.formLabel}>Gateway Type</Typography>

              <Box display="flex" flexDirection="column" gap="12px">
                <Checkbox
                  label="This is a test gateway"
                  checked={formData.isLive === false}
                  onChange={() => {
                    setFormData((prev) => ({
                      ...prev,
                      isLive: false
                    }));
                  }}
                  size="small"
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }
                  }}
                />

                <Checkbox
                  label="This is a live gateway"
                  checked={formData.isLive === true}
                  onChange={() => {
                    setFormData((prev) => ({
                      ...prev,
                      isLive: true
                    }));
                  }}
                  size="small"
                />
              </Box>
            </MuiBox>
          )}
          {formData.gateway === 'payConex' && (
            <>
              <MuiBox className={classes.formBox}>
                <InputField
                  label="Account ID"
                  value={formData.accountId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      accountId: e.target.value
                    }))
                  }
                  sx={{ width: '262px', height: '45px' }}
                />
              </MuiBox>
              <MuiBox className={classes.formBox}>
                <InputField
                  label="API Key ID"
                  value={formData.apiKeyId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      apiKeyId: e.target.value
                    }))
                  }
                  sx={{ width: '540px', height: '45px' }}
                />
              </MuiBox>
              <MuiBox className={classes.formBox}>
                <InputField
                  label="API Key Secret"
                  value={formData.apiKeySecret}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      apiKeySecret: e.target.value
                    }))
                  }
                  sx={{ width: '540px', height: '45px' }}
                />
              </MuiBox>
              <MuiBox className={classes.formBox}>
                <InputField
                  label="API Access Key"
                  value={formData.apiAccessKey}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      apiAccessKey: e.target.value
                    }))
                  }
                  sx={{ width: '540px', height: '45px' }}
                />
              </MuiBox>
            </>
          )}
          {formData.gateway === 'payConexPlus' && (
            <MuiBox>
              <MuiBox className={classes.formBox}>
                <InputField
                  label="Terminal ID"
                  value={formData.terminalId}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      terminalId: e.target.value
                    }))
                  }
                  sx={{ width: '262px', height: '45px' }}
                />
              </MuiBox>
              <MuiBox className={classes.formBox}>
                <InputField
                  label="Secret Key"
                  value={formData.secretKey}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      secretKey: e.target.value
                    }))
                  }
                  sx={{ width: '540px', height: '45px' }}
                />
              </MuiBox>
              <MuiBox className={classes.formBox}>
                <InputField
                  label="API Key"
                  value={formData.apiKey}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      apiKey: e.target.value
                    }))
                  }
                  sx={{ width: '540px', height: '45px' }}
                />
              </MuiBox>
            </MuiBox>
          )}
          {formData.kisIsMerchant && (
            <FeeInput
              label="Card Processing Fee Markup"
              amount={formData.cardProcessingFee.amount}
              percentage={formData.cardProcessingFee.percentage}
              onAmountChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  cardProcessingFee: { ...prev.cardProcessingFee, amount: v }
                }))
              }
              onPercentageChange={(v) =>
                setFormData({
                  ...formData,
                  cardProcessingFee: {
                    ...formData.cardProcessingFee,
                    percentage: v
                  }
                })
              }
              disabled={!canEditGatewaySettings}
            />
          )}
          <FeeInput
            label="KIS Transaction Fee"
            amount={formData.kisTransactionFee.amount}
            percentage={formData.kisTransactionFee.percentage}
            onAmountChange={(v) =>
              setFormData((prev) => ({
                ...prev,
                kisTransactionFee: {
                  ...prev.kisTransactionFee,
                  amount: v
                }
              }))
            }
            onPercentageChange={(v) =>
              setFormData({
                ...formData,
                kisTransactionFee: {
                  ...formData.kisTransactionFee,
                  percentage: v
                }
              })
            }
            disabled={!canEditGatewaySettings}
          />
          <FeeInput
            label="Default KIS Product Fee"
            amount={formData.defaultProductFee.amount}
            percentage={formData.defaultProductFee.percentage}
            onAmountChange={(v) =>
              setFormData({
                ...formData,
                defaultProductFee: {
                  ...formData.defaultProductFee,
                  amount: v
                }
              })
            }
            onPercentageChange={(v) =>
              setFormData({
                ...formData,
                defaultProductFee: {
                  ...formData.defaultProductFee,
                  percentage: v
                }
              })
            }
            disabled={!canEditGatewaySettings}
          />
          <VenueTaxesTable
            taxes={formData.venueTaxes}
            onCreate={() => {
              setVenueOverlayOpen(true);
              setVenueActionType('create');
            }}
            onEdit={(venueTax) => {
              setVenueActionType('edit');
              setVenueOverlayOpen(true);
              setVenueEditData(venueTax);
            }}
            onDelete={(tax) => {
              setShowDeleteOverlay(true);
              setVenueEditData(tax);
            }}
            venues={venues}
            disabled={!sortedEnvironments.length || !canEditPaymentSettings}
          />
          <MuiBox mt="30px">
            <Switch
              checked={formData.allowRefunds}
              checkedLabel="Allow refunds"
              uncheckedLabel="Allow refunds"
              disabled={!canEditPaymentSettings}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  allowRefunds: e.target.checked
                }))
              }
            />
          </MuiBox>
        </Box>
        <Box className={classes.buttonGroup}>
          <Button customSize="small" width={144} onClick={handleReset} disabled={isDisabled || !canEditPaymentSettings}>
            Reset
          </Button>
          <Button
            customSize="small"
            width={144}
            onClick={handleSave}
            disabled={isDisabled || !canEditPaymentSettings || !isSavable()}
          >
            Save
          </Button>
        </Box>
        {venueOverlayOpen && venueActionType && (
          <VenueTaxOverlay
            mode={venueActionType}
            onClose={() => {
              setVenueOverlayOpen(false);
              setVenueActionType(undefined);
              setVenueEditData(undefined);
            }}
            onSave={(newTax) => {
              setFormData((prev) => updateVenueTax(prev, newTax));
              setVenueOverlayOpen(false);
              setVenueEditData(undefined);
            }}
            venues={availableVenues}
            initialData={venueEditData}
            disabled={!canEditPaymentSettings}
          />
        )}
        {showDeleteOverlay && venueEditData && (
          <FormDialog
            open
            cancelButtonText="Cancel"
            cancelButtonAction={() => {
              setShowDeleteOverlay(false);
              setVenueEditData(undefined);
            }}
            confirmButtonAction={() => {
              setFormData((prev) => ({
                ...prev,
                venueTaxes: prev.venueTaxes.filter((t) => t.venueId !== venueEditData.venueId)
              }));
              setShowDeleteOverlay(false);
              setVenueEditData(undefined);
            }}
            confirmButtonText="Delete"
          >
            <Typography fontSize={24} maxWidth="524px" alignItems="center">
              Are you sure you want to delete this venue tax?
            </Typography>
          </FormDialog>
        )}
        {showWarning && (
          <FormDialog
            open
            confirmButtonText="Cancel"
            confirmButtonAction={() => {
              setShowWarning(false);
            }}
          >
            <Typography fontSize={24} maxWidth="524px" alignItems="center">
              Please select a Gateway type
            </Typography>
          </FormDialog>
        )}
        {/* RISKY CHANGE WARNING OVERLAY */}
        {showRiskyChangeWarning && (
          <FormDialog
            open
            cancelButtonText="Cancel"
            cancelButtonAction={() => setShowRiskyChangeWarning(false)}
            confirmButtonText="Continue"
            confirmButtonAction={() => {
              setShowRiskyChangeWarning(false);
              submitPaymentSettings();
            }}
          >
            <Typography fontSize={24} maxWidth="524px">
              Transactions have been performed in this environment, so changing these settings could cause problems. Are
              you sure you want to continue?
            </Typography>
          </FormDialog>
        )}
      </MuiBox>
    </MuiBox>
  );
};

export default PaymentsView;
